import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

import {
  PlatformSetting,
  apiAdminPlatformSettings,
  apiAdminSetPlatformSetting
} from './api';

/**
 * The sandbox knobs: how many sessions the platform holds, how long it holds
 * them, and how hard it works at once.
 *
 * Not customer-scoped -- there is one pool -- so this sits outside the customer
 * picker and only appears for SUPER_ADMIN or someone granted
 * MANAGE_PLATFORM_SETTINGS.
 *
 * Two things the design has to carry, because getting either wrong costs an
 * operator real time:
 *
 *   * WHEN a change lands. Memory and CPU cannot be applied to a running
 *     container; an operator who does not know that will restart one to make it
 *     take effect and throw away the loaded tables. Every field says so.
 *   * WHAT the server refused. The API rejects out-of-range values rather than
 *     clamping them, so the message it returns is the useful part -- it says
 *     the pool is provisioned for ten seats, not just "1 to 10".
 */
export default function PlatformSettings() {
  const [settings, setSettings] = useState<PlatformSetting[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);

  const load = () =>
    apiAdminPlatformSettings()
      .then((r) => {
        setSettings(r.settings);
        setDrafts(
          Object.fromEntries(r.settings.map((s) => [s.key, String(s.value)]))
        );
      })
      .catch((e) => setNotice({ kind: 'err', text: e.message }));

  useEffect(() => {
    load();
  }, []);

  const save = async (setting: PlatformSetting) => {
    const raw = drafts[setting.key];
    setSaving(setting.key);
    setNotice(null);
    try {
      const result = await apiAdminSetPlatformSetting(
        setting.key,
        Number(raw)
      );
      setSettings((prev) =>
        (prev ?? []).map((s) =>
          s.key === setting.key
            ? { ...s, value: result.value, is_default: result.value === s.default }
            : s
        )
      );
      setNotice({
        kind: 'ok',
        text:
          result.previous === result.value
            ? `${setting.key} is unchanged.`
            : `${setting.key}: ${result.previous} → ${result.value}. ` +
              (result.applies === 'immediately'
                ? 'In effect now.'
                : 'Applies to containers started from now on; the ones running keep their current limits.')
      });
    } catch (e) {
      // The server's message is the useful one -- it explains WHY, not just
      // that the number was wrong.
      setNotice({ kind: 'err', text: (e as Error).message });
      setDrafts((d) => ({ ...d, [setting.key]: String(setting.value) }));
    } finally {
      setSaving(null);
    }
  };

  if (!settings) {
    return (
      <Card className="p-4 text-sm text-muted-foreground">
        {notice?.kind === 'err' ? notice.text : 'Loading platform settings…'}
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="font-medium">Sandbox</div>
        <p className="text-xs text-muted-foreground">
          One pool serves every customer. These take effect without a deploy —
          the operational ones on the next check, the container limits on the
          next container.
        </p>
      </div>

      {notice && (
        <div
          className={`text-sm rounded-md px-3 py-2 ${
            notice.kind === 'ok'
              ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              : 'bg-destructive/10 text-destructive'
          }`}
        >
          {notice.text}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {settings.map((s) => {
          const dirty = drafts[s.key] !== String(s.value);
          return (
            <Card key={s.key} className="p-3">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs font-mono">{s.key}</code>
                    {!s.is_default && (
                      <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-500/15 text-amber-700 dark:text-amber-400">
                        changed from {s.default}
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
                      {s.applies === 'immediately'
                        ? 'applies immediately'
                        : 'new containers only'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.description}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    allowed {s.min}–{s.max} · default {s.default}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Input
                    className="w-28 font-mono tabular-nums"
                    inputMode="numeric"
                    value={drafts[s.key] ?? ''}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [s.key]: e.target.value }))
                    }
                    aria-label={s.key}
                  />
                  <Button
                    size="sm"
                    disabled={!dirty || saving === s.key}
                    onClick={() => save(s)}
                  >
                    {saving === s.key ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
